import React, { useEffect, useRef, useState } from 'react';
import {
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader,
  AlertDialogBody, AlertDialogFooter, Button, Text,
} from '@chakra-ui/react';
import { mutate } from 'swr';
import xhr from 'xhr';
import { getTechnicalCareerSchool } from 'utils/technicalCareerSchool';
import { getStudentSchoolOptions } from 'utils/studentSchools';

const schoolLabel = school => getStudentSchoolOptions(school).find(option => option.value === school)?.label || school;

export default function useTechnicalSchoolAssignment(studentId) {
  const [change, setChange] = useState(null);
  const resolver = useRef(null);
  const cancelRef = useRef(null);
  useEffect(() => () => { resolver.current?.(false); }, []);

  const finish = confirmed => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setChange(null);
  };

  const confirmSchoolAssignment = async career => {
    const school = getTechnicalCareerSchool(career);
    if (!school) return true;
    // Mantener el colegio del bachillerato; el API también comprueba esta regla al crear la matrícula.
    const grades = await xhr(`/grade-enrollments?filters[student][id][$eq]=${studentId}&filters[status][$in][0]=estudiando&filters[status][$in][1]=promocion_pendiente&fields[0]=id&pagination[limit]=1`);
    if (!Array.isArray(grades.data.data)) throw new Error('No se pudo consultar el bachillerato del estudiante.');
    if (grades.data.data.length) return true;
    // Consultar al guardar: el colegio pudo cambiar desde que se abrió el perfil.
    const response = await xhr(`/students/${studentId}?fields[0]=school`);
    const student = response.data.data?.attributes || response.data.data;
    if (!student) throw new Error('No se pudo consultar el colegio actual del estudiante.');
    if (!student.school || student.school === school) return true;
    return new Promise(resolve => {
      resolver.current = resolve;
      setChange({ previous: student.school, next: school, career: career.name });
    });
  };

  const refreshStudent = () => mutate(key => key && typeof key === 'object' && String(key.id_est) === String(studentId))
    .catch(error => console.error('No se pudo refrescar el perfil después de la matrícula', error));

  const schoolChangeDialog = (
    <AlertDialog isOpen={Boolean(change)} leastDestructiveRef={cancelRef} onClose={() => finish(false)} isCentered>
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader>Cambiar colegio del estudiante</AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={2}>Al matricular <strong>{change?.career}</strong>, el colegio cambiará automáticamente:</Text>
            <Text mb={2}><strong>{schoolLabel(change?.previous)}</strong> → <strong>{schoolLabel(change?.next)}</strong>.</Text>
            <Text>¿Deseas continuar con la matrícula?</Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={() => finish(false)}>Cancelar</Button>
            <Button colorScheme="purple" ml={3} onClick={() => finish(true)}>Continuar matrícula</Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );

  return { confirmSchoolAssignment, refreshStudent, schoolChangeDialog };
}
